// The AI Agent project page (pages/AIAgentProjectDetailsPage.jsx), merged into the
// main dictionary under `project`: t('project.<key>'). Same keys in en, es and pt.
export default {
  seo: {
    title: '{title} - AI Agent Video Project',
    fallbackTitle: 'AI Agent Video Project Details',
    description: 'View and customize your AI-generated video. Edit captions, replace images, adjust scenes, and export your professional AI-created content.',
  },

  tour: {
    preview: { title: 'Your video', lead: 'This is the video, as it will be exported.', hint: 'A change to a scene shows here at once.' },
    scenes: { title: 'Scenes', lead: 'Open a scene to change its words, its voice or its pictures.', hint: 'The pictures can be swapped, redrawn or animated.' },
    export: { title: 'Your file', lead: 'Press Export to render the file; download it from here when it is done.', hint: 'Change something, then export again.' },
    editScene: { title: 'The words and the voice', lead: 'Change what is said, have it spoken again, or fix the captions.', hint: 'Two tabs: the narration and the captions.' },
    slides: { title: 'The pictures', lead: 'Choose a picture to swap it, redraw it or animate it.', hint: 'Drag to reorder; the bin removes one.' },
    back: 'Previous',
    close: 'Close',
    last: 'Finish',
    next: 'Next',
    skip: 'Skip tour',
  },

  load: {
    failed: 'Failed to load project',
    loading: 'Loading project...',
    failedTitle: 'Failed to load project',
    backToProjects: 'Back to Projects',
  },

  header: {
    back: 'Back',
    idea: 'The idea',
    scenes: '{n} scenes',
    scenesOne: '{n} scene',
    studio: "Open in the editor",
    studioShort: 'Studio',
  },

  refresh: {
    successTitle: 'Success',
    successBody: 'Changes updated successfully.',
    savedTitle: 'Update saved',
    savedBody: 'Please reload the page to see changes.',
  },

  export: {
    completeTitle: 'Export complete!',
    completeBody: 'Your AI Agent video has been rendered successfully.',
    renderFailed: 'Rendering failed',
    startedTitle: 'Export started',
    startedBody: 'Your video is being rendered. This may take a few minutes.',
    startFailed: 'Failed to start export',
    failedTitle: 'Export failed',
    tryAgain: 'Try again',
    failedLabel: 'Export failed:',
    exporting: 'Exporting...',
    reexport: 'Re-export',
    exportVideo: "Export video",
    exportShort: 'Export',
    exportFree: 'Export free · with watermark',
    exportFreeHint: 'Your plan includes this video. Upgrading removes the watermark.',
    viewExport: "View export",
    view: 'View',
    rendering: 'Rendering video...',
    queued: 'Waiting in line to export…',
    renderedTitle: "Your video is ready",
    editStyle: "Back to editing",
    askChange: "Want a change? Ask here…",
    download: "Download video",
    downloadBig: 'Save the video to your device',
    downloadStarted: "The download has started. Look for the video in your Downloads.",
    downloadAgain: "Save it again",
    downloadHint: 'An MP4, ready to post on TikTok, Reels or Shorts.',
    designing: "Adding the motion design: about 3 minutes",
    waitsForDesign: "Your export starts as soon as the motion design is in",
    noVideoTag: 'Your browser does not support the video tag.',
    renderedBody: 'Your AI-generated video has been rendered. You can download the rendered video or edit it to create a new version.',
    exportedAt: 'Exported: {date}',
    size: ' • Size: {size} MB',
    confirmTitle: "Export again?",
    confirmBody: 'This project already has an exported video. Exporting again will replace it with a new render.',
    cancel: 'Cancel',
    confirm: 'Re-export',
  },

  animation: {
    animate: "Animate slides",
    cancelled: 'Animation cancelled. Completed {done} of {total} slides.',
    allDone: 'Successfully animated all {n} slides!',
    partial: 'Animated {done} of {total} slides. {failed} failed.',
    progress: 'Animating {done} of {total}…',
    leaveWarning: 'Slides are still being animated. If you leave, the ones not started yet stay as pictures.',
  },

  voice: {
    sameTitle: 'Same voice selected',
    sameBody: 'Please select a different voice to continue.',
    limitTitle: 'Usage limit reached',
    limitBody: "You've reached your voice regeneration limit.",
    notEnough: '{needed} scenes need the new voice, and this video has {left} voice redos left.',
    alreadyTitle: 'The voice is already changing',
    alreadyBody: "Here's how far it got.",
    doneTitle: 'Voice changed successfully!',
    doneBody: 'All {n} scenes have the new voice. Changed your mind? Back to {voice} puts the old one back, at no cost.',
    failedTitle: 'Voice change failed',
    failedBody: 'The video keeps its voice, and nothing was counted. Try again.',
    failedEdited: 'The video was edited while its voice changed, so it keeps its voice. Nothing was counted.',
    changeAll: 'Change voice for all scenes',
    changing: 'Changing...',
    change: 'Change Voice',
    progressTitle: 'Changing Voice...',
    progressBody: 'Every scene is voiced again, four at a time. The video keeps its voice until all of them are ready.',
    progress: 'Progress',
    canClose: 'You can close this, or the page: it keeps going, and the new voice is on the video when you come back.',
    close: 'Close',
    now: 'Voice: {voice}',
    backTo: 'Back to {voice}',
    undoneTitle: 'The voice is {voice} again',
    undoFailed: "Couldn't go back to the old voice. Try again.",
    ownVoice: 'Your voice',
    pickerTitle: 'Change narrator voice',
    pickerBody: 'Select a new voice for all scenes in this project',
    unknown: 'Unknown',
    voices: 'Voices',
    voicesTitle: 'Voices',
    voicesBody: 'The narrator and each character have a voice of their own.',
    narrator: 'Narrator',
    narratorBlurb: 'Reads the story',
    changeOne: 'Change',
    speakers: '{n} voices in this video',
    whoFemaleChild: 'Girl',
    whoMaleChild: 'Boy',
    whoFemaleYoung: 'Young woman',
    whoMaleYoung: 'Young man',
    whoFemaleAdult: 'Woman',
    whoMaleAdult: 'Man',
    whoFemaleOlder: 'Older woman',
    whoMaleOlder: 'Older man',
    whoCharacter: 'Character',
    inScenesOne: '1 scene',
    inScenesMany: '{n} scenes',
    pickerCharacterTitle: `Change {name}'s voice`,
    pickerCharacterBody: `Pick a new voice for {name}. Only {name}'s lines are voiced again.`,
    confirmCharacterTitle: `Change {name}'s voice?`,
    confirmCharacterBody: `Only {name}'s lines are voiced again. The narrator and the other characters keep their voices.`,
    impactCharacterOne: `{name}'s lines in {n} scene will be voiced again.`,
    impactCharacterMany: `{name}'s lines in {n} scenes will be voiced again.`,
    canUndoCharacter: `You can go back to {name}'s current voice afterwards, at no cost`,
    progressCharacterTitle: `Changing {name}'s voice…`,
    progressCharacterBody: 'Only the scenes where {name} speaks are voiced again. The video keeps the old voice until all of them are ready.',
    progressScenes: `Changing {name}'s voice… {done} of {total} scenes`,
    doneCharacterTitle: `{name}'s voice changed!`,
    doneCharacterBody: `{name}'s lines in {n} scenes have the new voice. Changed your mind? Go back from the bar above, at no cost.`,
    nowCharacter: '{name}: {voice}',
    backToCharacter: 'Go back to {voice} for {name}',
    undoneCharacterTitle: '{name} has the voice {voice} again',
    sceneVoices: '{n} voices',
    editHint: 'Words in quotation marks are said by the characters: {characters}. Keep the quotation marks around what a character says.',
  },

  watermark: {
    remove: 'Remove Watermark',
    premium: 'PREMIUM',
    dialogTitle: 'Remove Watermark with {plan}',
    dialogBody: 'Upgrade to {plan} to remove watermarks from all your AI-generated videos and unlock unlimited features.',
    noWatermarks: 'No watermarks',
    noWatermarksBody: 'Export clean, professional videos',
    hours: '{hours} hours of video processing',
    minutes: 'Up to {minutes} minutes per month',
    agentVideos: '{n} AI Agent videos',
    reprompts: '{n} reprompting attempts',
    continueFree: 'Continue as Free',
    seePricing: 'See Pricing',
  },

  upgrade: {
    title: 'Slide animation is a premium feature',
    body: 'Convert your static slides to cinematic videos with AI. Upgrade to unlock this feature.',
    unlimited: 'Unlimited slide animations',
    unlimitedBody: 'Animate as many slides as you want',
    batch: 'Batch animate all slides',
    batchBody: 'Convert all images to videos with one click',
    models: 'Premium AI models',
    modelsBody: 'Access to the latest animation models',
    later: 'Maybe Later',
    viewPricing: 'View Pricing',
  },

  scenes: {
    none: 'No scenes available',
    tab: 'Scenes',
    heading: 'Scenes',
    noTranscriptTitle: 'No transcription available',
    noTranscriptBody: "This scene doesn't have transcription data to edit.",
    loadFailedTitle: 'Failed to load scene data',
    loadFailedBody: 'Could not load scene data.',
    updatedTitle: 'Scene updated',
    updatedBody: 'Refreshing preview...',
    deleteFailed: 'Failed to delete slide. Please try again.',
    reorderFailed: 'Failed to reorder slides. Please try again.',
    sceneAlt: 'Scene {n}',
    slideAlt: 'Slide {n}',
    editScene: 'Edit scene',
    visualAssets: 'Visual Assets ({n})',
    moveUp: 'Move scene up',
    moveDown: 'Move scene down',
    deleteScene: 'Delete scene',
    deleteSceneTitle: 'Delete scene {n}?',
    deleteSceneBody: 'Its narration, pictures, sound and text go with it. The rest of the video stays as it is.',
    deleteSceneConfirm: 'Delete scene',
    arrangeFailed: 'Could not change the scenes. Try again.',
    deleteSlide: 'Delete slide',
    video: 'Video',
    addSlide: 'Add new slide',
    add: 'Add',
    deleteTitle: 'Delete Slide?',
    deleteBody: 'Are you sure you want to delete slide {slide} from scene {scene}? This action cannot be undone.',
    delete: 'Delete',
    cancel: 'Cancel',
    slideOf: 'Slide {n} of {count}',
    moveEarlier: 'Move earlier',
    moveLater: 'Move later',
  },

  captions: {
    toolMusic: 'Music',
    toolSound: 'Sound',
    toolText: 'Text',
    toolCaptions: 'Captions',
    tab: "Style",
    heading: "Caption style",
    text: 'Text',
    highlight: 'Highlight',
    stroke: 'Stroke',
    customize: 'Customize',
    styleLabel: 'Style',
    colors: 'Colours',
    show: 'Show captions',
    offHint: 'No captions on this video. Turn them back on to pick a style.',
  },

  // A remix: an AI Agent video built around an AI-clipping clip
  motionGraphic: {
    title: "Motion graphic",
    hint: "The animated graphic that plays over this scene. Change its words and numbers, or use the picture instead.",
    kinds: { kinetic: "Big type", stat: "Number", bars: "Bar chart", line: "Trend line", compare: "Compare", timeline: "Timeline", steps: "Steps", donut: "Shares", map: "Map", pictogram: "Share as figures", checklist: "Checklist", stamp: "Stamp", dossier: "Document card", quote: "Quote page", ranking: "Ranking", headline: "Headline", equation: "Calculation", column: "Column sum", numberline: "Number line", fraction: "Fraction bars", placevalue: "Place value", counters: "Counters", clock: "Clock", calendar: "Calendar", plot: "Graph", shape: "Shapes" },
    heading: "Title",
    label: "Name {n}",
    labelShort: "Name",
    value: "Value {n}",
    valueShort: "Value",
    save: "Save",
    saved: "Saved",
    usePicture: "Use the picture instead",
    remove: { stamp: "Remove the stamp", dossier: "Remove the card", quote: "Remove the page", headline: "Remove the headline", clock: "Remove the clock", calendar: "Remove the calendar", map: "Remove the map" },
    failed: "Could not save. Try again.",
    refused: "A number is needed in each value.",
  },
  remix: {
    badge: 'Remix',
    stickers: 'Stickers',
    addSticker: 'Sticker',
    editNotes: 'Edit notes',
    editCaptions: 'Captions',
    noNotes: 'No notes over this part.',
    notesHint: 'Notes show over the clip between {from} and {to}. Type when each starts (m:ss) and for how many seconds.',
    noteAt: 'Starts at (m:ss)',
    noteLength: 'Seconds on screen',
    noteText: 'What the note says',
    addNote: 'Add a note',
    deleteNote: 'Delete this note',
    cancel: 'Cancel',
    noteNeedsWords: 'Every note needs some words.',
    noteOutside: '“{text}” has to start and end inside this part, for half a second at least.',
    noteOverlap: '“{text}” overlaps “{other}”.',
    titleCard: 'Title card',
    addTitleCard: "Add a title card",
    titleSize: 'Size',
    titlePlace: 'Place',
    placeTop: 'Top',
    placeMiddle: 'Middle',
    titleCardHint: 'Big text over the first 3 seconds, to stop the scroll.',
    kicker: 'Small line above (e.g. HOT TAKE)',
    headline: 'Headline',
    save: 'Save',
    saved: 'Saved',
    titleCardOff: "Leave the headline empty to remove the title card.",
    titleCardFailed: 'Could not save. Try again.',
    clipScene: 'Your clip',
    clipSceneHint: 'Plays with its own sound · {length}',
    clipPart: 'Your clip · part {n} of {total}',
    clipRange: '{from}–{to} of the clip, with its own sound',
    onScreen: 'On screen',
    fromClip: 'From your clip',
    source: 'Remix of “{title}”',
  },

  // The clip workspace's clean-up settings (ClipWorkspace), and caption files
  // there and in Auto Caption
  cleanClip: {
    title: 'Clean up',
    cutPauses: 'Cut pauses and filler sounds',
    cutPausesHint: 'Shortens long pauses and takes out sounds like "um". The captions follow.',
    saves: 'Saves {seconds} s: {to} instead of {from}',
    nothingToCut: 'No long pauses or filler sounds in this clip.',
    studioSound: 'Studio sound',
    studioSoundHint: 'Cleans up the voice and evens out the volume. Applied when you export.',
    captionFile: 'Caption file',
    downloadCaptions: 'Download captions',
    formatSrt: 'SRT (YouTube, most players)',
    formatVtt: 'VTT (web)',
    formatTxt: 'Text only',
    formatJson: 'JSON (word timings)',
    cutWord: '"{word}" is cut from the video. Click to put it back.',
    cutPause: 'A {seconds} s pause is cut. Click to keep it.',
    finding: 'Listening for pauses…',
    findFailed: "Couldn't read the clip's sound to find its pauses",
    saveFailed: "Couldn't save the cuts",
  },

  // The clip workspace's Polish section (ClipWorkspace/ClipPolishPanel)
  clipPolish: {
    keywords: "Key words",
    keywordsHint: "Coloured in the captions, so the point lands with the sound off. Click a word in the transcript to add or remove one.",
    intro: "A title card for the first seconds, and a sticker or a zoom at the moments the clip leans on.",
    write: "Write with AI",
    writing: "Writing…",
    rewrite: "Rewrite",
    writeFailed: "Couldn't write the polish. Try again.",
    rewritten: "Rewritten: a new title card, moments and key words. Your text, B-roll and music stayed.",
    putBack: "Put the old ones back",
    noWords: "This clip has no words to work from yet.",
    titleCard: "Title card",
    kicker: "Small line on top",
    headline: "Headline",
    addSticker: "Sticker",
    moments: "Moments",
    momentsCount: "{count} moments",
    stickers: "Stickers",
    zoom: "Zoom in at moments",
    zoomMoment: "Zoom in here",
    noSticker: "No sticker",
    removeMoment: "Remove this moment",
    addMoment: "Add a moment here",
    jumpTo: "Play from {time}",
    saveFailed: "Couldn't save the polish",
    noMoments: "No moments yet. Play to a word and add one.",
    keywordsHintTouch: "Coloured in the captions, so the point lands with the sound off. Tap a word in the transcript to add or remove one.",
  },
  agentSound: {
    voice: "Narrator voice",
    voiceOff: "Off",
    title: "Sound effects",
    hint: "Hits, whooshes and ambience placed on the narration. Play the preview to hear your changes; they save by themselves.",
    legacy: "This video was made before editable sound effects.",
    start: "Add sound effects",
    enabled: "Sound effects",
    level: "Overall level",
    addAtPlayhead: "Add a sound at the playhead",
    addingAt: "Adding to scene {scene} at {time}",
    pick: "Choose a sound",
    close: "Close",
    listen: "Listen",
    stop: "Stop",
    sceneN: "Scene {n}",
    ambience: "Ambience",
    noAmbience: "None",
    noCues: "No sound effects in this scene.",
    remove: "Delete this sound",
    earlier: "0.1 s earlier",
    later: "0.1 s later",
    time: "Time in the scene",
    jump: "Jump the preview here",
    volume: "Volume",
    saveFailed: "Couldn't save the sound effects",
    groups: {
      hits_and_transitions: "Hits & transitions",
      cartoon_and_interface: "Cartoon & interface",
      ambience: "Ambience",
      nature: "Nature",
      people: "People",
      objects: "Objects",
      action: "Action",
      animals: "Animals",
      vehicles: "Vehicles",
    },
  },
  editorRail: {
    transcript: "Transcript",
    label: "Editor tools",
    scenes: "Scenes",
    captions: "Captions",
    text: "Text",
    sound: "Sound",
    music: "Music",
    scenesHint: "The video in order: its words and pictures. Click a scene to see it on the timeline.",
  },
  videoSettings: {
    title: "This video",
    video: "Video",
    format: "Format",
    length: "Length",
    voice: "Voice",
    titleCard: "Title card",
    none: "None",
    layers: "On the video",
    captions: "Captions",
    music: "Music",
    sound: "Sound effects",
    text: "Text",
    design: "Design",
    off: "Off",
    sounds: "{n} on the video",
    texts: "{n} on the video",
    hint: "Click anything on the timeline to edit it.",
  },
  agentTranscript: {
    sceneN: "Scene {n}",
    saveFailed: "Couldn't save the words",
    locked: "The words open again when the new voice is ready.",
  },
  versions: {
    button: "History",
    open: "Version history",
    title: "Version history",
    hint: "Every change you or the agent make is kept here. Undo one change and keep everything after it, or restore the whole video as it was at a version. Both are versions too, so you can go back again.",
    loading: "Loading…",
    empty: "No changes yet. Every change you or the agent make will be listed here, and any of them can be brought back.",
    current: "Current",
    restore: "Restore",
    undo: "Undo this change",
    undone: "v{n} undone. Everything after it is kept.",
    undoKept: "Some of it changed again later, so that part stays as it is now.",
    undoScenes: "This version added, removed or moved scenes: restore the version before it instead.",
    undoChangedSince: "Everything this version changed was changed again later, so there is nothing to undo.",
    undoFailed: "That version couldn't be undone. Nothing changed.",
    restoring: "Restoring…",
    restored: "Restored to v{n}.",
    restoreFailed: "The version couldn't be restored. Nothing changed.",
    cameBack: "↩ Back as before: {what}",
    justNow: "just now",
    minutesAgo: "{n} min ago",
    by: { user: "You", agent: "Agent", restore: "Restore", system: "Generated" },
    change: {
      music_set: "Music: {to}",
      music_volume_set: "Music volume: {to}%",
      caption_style_set: "Caption style: {to}",
      caption_size_set: "Caption size: {to} px",
      voice_set: "Voice: {to}",
      music_unset: "Music: the default",
      music_volume_unset: "Music volume: the default",
      caption_style_unset: "Caption style: the default",
      caption_size_unset: "Caption size: the default",
      voice_unset: "Voice: the default",
      original: "The video as generated",
      changed: "Changed",
      restored: "Restored to v{to}",
      reverted: "Undid v{version}",
      music: "Music: {from} → {to}",
      music_volume: "Music volume: {from}% → {to}%",
      voice_level: "Narrator voice: {from}% → {to}%",
      music_off: "Music off",
      music_on: "Music on",
      caption_style: "Caption style: {from} → {to}",
      caption_size: "Caption size: {from} → {to} px",
      caption_color: "Caption colours",
      caption_position: "Caption position",
      caption_look: "Caption look (font, animation or case)",
      caption_box_on: "A box behind the captions",
      caption_box_off: "No box behind the captions",
      caption_default: "Captions back to the default style",
      text_added: "Scene {scene}: text “{text}” added",
      text_removed: "Scene {scene}: text “{text}” removed",
      text_changed: "Scene {scene}: text “{text}” changed",
      captions_off: "Captions off",
      captions_on: "Captions on",
      voice: "Voice: {from} → {to}",
      narration: "Scene {scene}: narration rewritten and voiced again",
      voice_redone: "Scene {scene}: voiced again",
      caption_words: "Scene {scene}: caption words",
      picture_added: "Scene {scene}: a picture or clip added",
      picture_removed: "Scene {scene}: a picture removed",
      picture_changed: "Scene {scene}: picture {picture} changed",
      picture_animated: "Scene {scene}: picture {picture} animated",
      pictures_reordered: "Scene {scene}: pictures reordered",
      pictures_changed: "Scene {scene}: pictures",
      title_card: "Title card",
      title_card_added: "Title card added",
      title_card_removed: "Title card removed",
      graphic: "Scene {scene}: graphic changed",
      graphic_added: "Scene {scene}: {kind} graphic added",
      graphic_removed: "Scene {scene}: graphic removed",
      beats: "Scene {scene}: beats",
      light_leak_on: "Scene {scene}: light leak on",
      scene_tail: "Scene {scene}: silence at the end changed",
      light_leak_off: "Scene {scene}: light leak off",
      design_redone: "Scene {scene}: design made again",
      design_removed: "Scene {scene}: design taken away",
      design_off: "Scene {scene}: design off",
      design_on: "Scene {scene}: design on",
      design_hidden: "Scene {scene}: design elements hidden ({count})",
      design_hidden_one: "Scene {scene}: design element “{text}” hidden",
      design_shown: "Scene {scene}: design elements brought back ({count})",
      design_shown_one: "Scene {scene}: design element “{text}” brought back",
      design_edited: "Scene {scene}: design elements changed ({count})",
      design_edited_one: "Scene {scene}: design element “{text}” changed",
      texts: "Scene {scene}: texts",
      sounds: "Scene {scene}: sound effects",
      scene_removed: "Scene {scene} removed",
      scene_added: "A scene added",
      scenes_reordered: "Scenes reordered",
    },
  },
  assistant: {
    timeline: "Timeline",
    modes: { agent: "Agent", script: "Script", scenes: "Scenes", style: "Style" },
    welcome: "Your video is ready: {scenes} scenes, {length}. Tell me what to change. Pause on a moment to ask about it, or press Point and click the picture to show me a spot.",
    welcomeShort: "Your video is ready. What should I change?",
    emptyTitle: "What should we change?",
    emptyBody: "Ask in your own words, or point at the video.",
    stageIdea: "Idea",
    stageScript: "Script",
    stageScenes: "Scenes",
    stageVideo: "Video",
    words: "{n} words",
    scenesPictures: "{scenes} scenes · {pictures} pictures",
    placeholder: "Ask for a change…",
    send: "Send",
    point: "Point at the video",
    pointHint: "Click the video where you mean",
    pointShort: "Point",
    selectedScene: "Selected",
    changedHere: "Changed in this chat",
    videoBigger: "Bigger video",
    videoSmaller: "Smaller video",
    panelShow: "Show the panel",
    panelHide: "Hide the panel",
    askOther: "Or say it your way…",
    whole: "Whole video",
    at: "{time} · Scene {scene}",
    picture: "Scene {scene} · picture {picture}",
    clearFocus: "Stop pointing",
    thinking: "Thinking…",
    restoredNote: "↩ The video went back to v{to}. Changes made after it are gone, so the Undo buttons above are off.",
    undidNote: "↩ v{n} was undone; the changes after it stay. The Undo buttons above are off.",
    unfinished: "I could not finish this, so I changed nothing. Ask again in smaller steps, or tell me which part matters most.",
    progress: { again: "Looking at the result…", look: "Looking at scene {scene} {time}", change: "Making: {what}", research: "Searching the web…", propose: "Proposing: {what}" },
    failed: "I couldn't answer just now. Try again.",
    limit: "That's all the requests for today. Try again tomorrow.",
    makeIt: "Make it",
    notNow: "Not now",
    costEdit: "Uses 1 AI edit",
    costAnimate: "Uses 1 animation",
    costVoice: "Re-voices every scene",
    making: "Making it…",
    meaning: "In your words",
    makingFor: "Making it… {seconds} s (usually about {usual} s)",
    keep: "Made: it is in the video",
    undo: "Undo",
    doneExcept: "Done, except the steps marked ✕.",
    notOnScreen: "Not on screen at {time}: {why}.",
    stillOnScreen: "Still on screen at {time}.",
    slow: "The model is slow right now, so I changed nothing. Send your request again in a moment.",
    hiddenBy: { graphic: "the graphic covers it", title_card: "the title card covers it", keyword: "the captions already show these words", none: "nothing is drawn there" },
    footageCost: "Free stock video",
    footagePick: "Hover or tap a clip to watch it, then Use to add it to the scene.",
    footagePickReplace: "Hover or tap a clip to watch it, then Use to put it in place of picture {picture}.",
    footageLooking: "Looking for clips…",
    footageNone: "No clips found for this. Ask with other words.",
    footageMore: "Other clips",
    footageUse: "Use",
    footageWatch: "Watch this clip",
    footageWhereAdded: "Picture {picture} of scene {scene}, at {at}",
    footageWhereReplaced: "In place of picture {picture} of scene {scene}, at {at}",
    footageAdded: "Clip added",
    footageFailed: "The clip couldn't be added. Nothing changed.",
    undoAll: "Undo this answer's changes",
    newChat: "New chat",
    chats: "Chats",
    chatsTitle: "Your chats about this video",
    backToChat: "Back to the chat",
    noChats: "No chats yet.",
    openNow: "Open now",
    requestsOne: "1 request",
    requestsN: "{n} requests",
    deleteChat: "Delete this chat",
    undone: "Undone",
    skipped: "You chose not now. Nothing changed.",
    removedDone: "Removed from the video",
    replacedDone: "In the video. Undo puts the old one back",
    before: "Before",
    after: "After",
    sameWords: "Same words, voiced again",
    needsMore: "This needs more than your plan has left for this video. Here's what Pro gives.",
    pictureFailed: "The picture couldn't be made. Nothing changed.",
    wordMissing: "I couldn't find that word in scene {scene}.",
    cantDo: "I couldn't do this step.",
    voiceOpened: "Confirm the new voice in the window.",
    working: "Working on it…",
    done: "Done",
    voiceRunning: "Changing the voice · {done} of {total} scenes",
    voiceDone: "Done: every scene has the new voice.",
    voiceFailed: "The voice couldn't be changed. Nothing changed.",
    sameVoice: "The video already has this voice.",
    costRemoveScene: "Takes the scene out for good",
    costRemovePicture: "Takes this picture out of scene {scene}",
    costFramePicture: 'Keeps this spot of the picture in view in scene {scene}',
    costWholePicture: 'Shows the whole picture, uncropped, in scene {scene}',
    pictureGone: "That picture is no longer in the scene.",
    onlyPicture: "It is the scene's only picture: replace it instead.",
    costReplace: "Free: it takes the place of “{name}” on scene {scene}",
    costTranslate: "Uses one of your plan's videos",
    costExport: "Makes the video file",
    costNewVideo: "Opens the create page with this idea; making it uses one of your plan's videos",
    newVideoOpened: "Opened on the create page",
    costVersion: "Free: History keeps this too",
    costDesign: "Free: scene {scene} is designed again (about a minute)",
    translateStarted: "Started: it is made as a new video",
    exportStarted: "Exporting: the file comes under the video",
    openTranslation: "Open the {language} version",
    costRewrite: "Re-voices this scene",
    costScene: "Adds a new voiced scene with its pictures (about half a minute)",
    attachPhoto: "Attach a photo of your own",
    attachPhotoShort: "Photo",
    photoFailed: "The photo did not upload",
    removePhoto: "Remove the photo",
    costPhoto: "Adds your photo to scene {scene} (free)",
    costSceneVideo: "This scene costs one of your plan's AI Agent videos: make it?",
    sceneFailed: "The new scene could not be made",
    sceneFull: "This video is as long as its plan allows. Pro makes videos up to 10 minutes.",
    rewriteKept: "To change it again, open Script.",
    rewriteFailed: "The scene couldn't be rewritten. Nothing changed.",
    withGraphic: "With a {kind} graphic on it",
    suggestions: ["Make the first 3 seconds hit harder", "Bigger captions", "Lower the music", "Try another voice"],
  },
  agentTimeline: {
    rows: { scenes: "Scenes", pictures: "Pictures", graphics: "Graphics", design: "Design", text: "Text", sound: "Sound", music: "Music" },
    kinds: { scene: "Scene", clip: "Your clip", slide: "Picture", hook: "Title card", visual: "Motion graphic", beat: "Beat", leak: "Light leak", text: "Text", sfx: "Sound effect", music: "Music",
      pop: "Word pop", number: "Number", icon: "Sticker", lower: "Lower third", burst: "Burst", count: "Count", glyph: "Glyph", zoom: "Punch-in", reframe: "Reframe",
      design: "Design element", template: "Motion piece", effect: "Effect", emoji: "Emoji", map: "Map", camera: "Picture move", drawing: "Drawing", shape: "Shape" },
    hidden: "Hidden", pictureMove: "Picture {n} move",
    undo: "Undo", redo: "Redo", zoomIn: "Zoom in", zoomOut: "Zoom out",
    remove: "Remove", muteMusic: "Turn the music off", deletePicture: "Delete picture", lastPicture: "A scene keeps at least one picture.",
    editWords: "Edit words", captionsTiming: "Edit captions", addPicture: "Add picture", deleteScene: "Delete scene", earlier: "Earlier", later: "Later",
    replacePicture: "Replace", animate: "Animate",
    clipHint: "Your own clip plays here with its sound. Its captions are edited in the scene list.",
    leakHint: "A flash of light over the cut into this scene.",
    beatHints: { camera: "A camera move on the picture at this moment. Drag it on the timeline to move it.", content: "Drawn over the picture as the word is said. Drag it on the timeline to move it." },
    words: "Words", number: "Number", moment: "At",
    undoHint: "Every change can be undone: ⌘Z, or the arrows over the timeline.",
    voice: 'Voice',
    saveFailed: "The change could not be saved. Try again.",
  },
  designDrag: {
    move: "Move {name}",
    resize: "Resize {name}",
    hint: "Drag to move it, or drag its corner to resize it. Arrow keys move it 1 px (10 px with Shift). Esc cancels.",
    hintMove: "Drag to move it. Arrow keys move it 1 px (10 px with Shift). Esc cancels.",
  },
  agentDesign: {
    title: "Design",
    hint: "What the motion designer drew on each scene. Open a piece to change its words, move it, or take it out.",
    scene: "Scene {n}",
    on: "On", off: "Off",
    offHint: "The scene shows its pictures and captions only.",
    reading: "Reading the design…",
    notRead: "The design could not be read.",
    none: "No designed scenes in this video.",
    empty: "Nothing to edit in this design.",
    hidden: "Hidden", edited: "Edited",
    show: "Show", hide: "Hide",
    reset: "Reset", resetAll: "Reset the whole design",
    redesign: "New design", redesignPlaceholder: "Tell the designer what to change…", redesignSend: "Design",
    redesigning: "Being designed again…",
    redesignFailed: "The designer could not be asked. Try again.",
    moment: "At", position: "Place", scale: "Scale", smaller: "Smaller", bigger: "Bigger",
    until: "Until", longer: "Stay longer", shorter: "Leave sooner", untilMax: "Room to {time}", timeNested: "{name} already has its own time: reset it first to change this one.", timeNestedGroup: "Its group already has its own time: reset that first to change this one.", endShared: "Its Until is shared with {names}: they end together, and Reset keeps it.", timeBehind: "Drawn behind: its time stays as the design made it. Set Layer to “As made” to change its time.", behindTimed: "{name} has a time of its own: reset its time to send this behind.",
    nudge: { left: "Move left", right: "Move right", up: "Move up", down: "Move down" },
    moveX: "Move across (px)", moveY: "Move up or down (px)", rotate: "Turn", rotateLess: "Turn back 5°", rotateReset: "Straighten", rotateMore: "Turn 5°", layer: "Layer",
    flowNote: "Part of the design's layout: it moves with the rest, so it cannot be moved, scaled or turned on its own.", nestedNote: "Inside a box of the design's: it can be moved, not scaled or turned here.",
    groups: { content: "Content", timing: "Timing", layout: "Layout" },
    layers: { flow: "This piece sits in the design's own layout, so it cannot be layered in front or behind.", back: "Behind", normal: "As made", front: "In front", frontHint: "Over everything in the scene, the person in the picture too.", backHint: "Behind the other pieces drawn with it, and behind the person in the picture.", trapped: "It cannot come further forward here: the group it is in keeps it in its own layer." },
    removeMove: "Take the picture's move away", restoreMove: "Put the picture's move back",
    remove: "Remove", restore: "Bring back",
    zone: { top: "Top", middle: "Middle", bottom: "Bottom" },
    prop: { kind: "Kind", count: "How many", speed: "Speed", text: "Words", words: "Words", title: "Title", sub: "Second line", kicker: "Small line", quote: "Quote", author: "Who said it", label: "Label",
      place: "Place", region: "Region", tag: "Tag", first: "First", second: "Second", date: "Date", char: "Emoji", emoji: "Emoji", mark: "Marked words",
      items: "Items (one a line)", value: "Number", prefix: "Before the number", suffix: "After the number", unit: "Unit", size: "Text size", zone: "Position",
      number: "Number", rank: "Rank", from: "From", to: "To", cps: "Letters a second", mode: "Mode", corner: "Corner", name: "Name" },
    settings: "{n} designed scenes",
    box: "Box",
    holdsNote: "Hiding this box hides what is inside it too.",
    style: { title: "Style", fill: "Fill", border: "Border", borderWidth: "Border width", corners: "Corners", round: "Round", line: "Line", color: "Colour",
      size: "Text size", weight: "Weight", weights: { 400: "Regular", 700: "Bold", 900: "Heavy" }, shadow: "Shadow", opacity: "Opacity", inkColor: "Text colour", accentColor: "Accent colour", clearColor: "Back to the design's colour", font: "Font", fonts: { design: "As made", display: "Title", body: "Body", mono: "Typed", hand: "Hand" }, textStyle: "Text", italic: "Italic", upper: "Capitals", align: "Alignment", aligns: { start: "Start", center: "Centre", end: "End" }, spacing: "Spacing", lineHeight: "Line height",
      reset: "Reset style" },
  },
  agentText: {
    title: "Text on the video",
    hint: "Put your own words on a scene: a title, a name, a number. Play the preview to see them; they save by themselves.",
    addAtPlayhead: "Add text at the playhead",
    full: "A scene can have {max} texts at most.",
    none: "No text yet. Move the preview to a moment and add one.",
    sceneN: "Scene {n}",
    placeholder: "Type the text",
    words: "The text",
    remove: "Delete this text",
    start: "Starts at",
    earlier: "0.1 seconds earlier",
    later: "0.1 seconds later",
    jump: "Jump the preview here",
    duration: "Shown for",
    position: "Position",
    positions: { top: "Top", middle: "Middle", bottom: "Bottom" },
    style: "Look",
    styles: { bold: "Bold", outline: "Outline", box: "White box", dark: "Dark box", yellow: "Yellow", card: "Card" },
    size: "Size",
    sizes: { s: "Small", m: "Medium", l: "Large" },
    saveFailed: "We couldn't save the text",
  },
  agentMusic: {
    title: 'Music',
    hint: "Background music from our own library, safe to post anywhere. It plays quietly under the narration.",
    off: 'No music',
    preview: 'Listen',
    stop: 'Stop',
    volume: 'Volume',
    saveFailed: 'Couldn\'t save the music',
    new: 'New',
    moodsLabel: 'Moods',
    moods: {
      suspense: 'Suspense',
      epic: 'Epic',
      uplifting: 'Uplifting',
      calm: 'Calm',
      playful: 'Playful',
      emotional: 'Emotional',
      tech: 'Tech',
      lofi: 'Lo-fi',
      oriental: 'Oriental',
      dramatic: 'Dramatic',
      energetic: 'Energetic',
      documentary: 'Documentary',
      romantic: 'Romantic',
      adventure: 'Adventure',
      happy: 'Happy',
    },
  },

  // The clip transcript's menu on a word (ClipWorkspace/ClipTranscript WordMenu)
  wordMenu: {
    highlight: "Colour it in the captions",
    unhighlight: "Stop colouring it",
    label: "Word: {word}",
    open: "Options for “{word}”",
    hint: "Click a word for options · double-click to fix it",
    fix: "Fix the word",
    startHere: "Start the clip here",
    endHere: "End the clip here",
    cutVideo: "Cut from the video",
    captionsOnly: "Remove from captions only",
    addEmoji: "Add an emoji",
    changeEmoji: "Change the emoji",
    hintTouch: "Tap a word for options",
  },

  // Who speaks, in the transcript (ClipWorkspace/SpeakerChip, utils/speakers.js)
  speakers: {
    default: "Speaker {n}",
    menu: "{name}: rename, or change who said this",
    rename: "Rename",
    nameLabel: "Name for {name}",
    saidBy: "Said by",
    newSpeaker: "Someone else",
    fromHere: "From here on, said by",
    color: "Caption colour",
    defaultColor: "Default colour",
    swatch: "Colour {n}",
  },

  // The transcript's emoji picker (ClipWorkspace/EmojiPicker)
  emojiPicker: {
    label: "Emoji for “{word}”",
    open: "Change the emoji after “{word}”",
    search: "Search, or type any emoji",
    remove: "Remove the emoji",
    none: "No emoji for that. Type or paste one",
    cannotShow: "That one can't be drawn in captions",
    typed: "Use this one",
  },

  // Transcript search, fixing a word everywhere, and the clip workspace's keys
  transcriptSearch: {
    open: "Search the transcript",
    placeholder: "Find a word",
    count: "{at} of {total}",
    none: "No matches",
    next: "Next match",
    prev: "Previous match",
    close: "Close search",
  },

  fixEverywhere: {
    offer: "Changed “{from}” to “{to}”. Change the other {count} too?",
    all: "Change all",
    dismiss: "Keep the others",
  },

  shortcuts: {
    tip: "Keys: Space plays, J and L skip 5 s, I and O set the start and end where the playhead is.",
  },

  // Undo and redo in the clip workspace (ClipWorkspace/clipHistory)
  history: {
    group: "History",
    undo: "Undo",
    redo: "Redo",
    undoWhat: "Undo: {what}",
    redoWhat: "Redo: {what}",
    what: {
      captions: "transcript fix",
      cuts: "cut from the video",
      polish: "polish edit",
      trim: "start and end",
      style: "caption style",
      titleCard: "title card",
      moments: "stickers and zooms",
      keywords: "key words",
      music: "music",
      text: "text",
      broll: "B-roll",
      autoZoom: "auto zoom",
    },
  },

  // A clip's own caption style in the workspace (saved on the clip)
  clipStyle: {
    customize: "Customize",
    customizeHint: "font, size, colours, position",
    saveFailed: "Couldn't save the caption style",
  },

  // Export or download every clip in the gallery at once (ClipWorkspace/ClipGallery)
  clipBulk: {
    exportAll: "Export all ({n})",
    downloadAll: "Download all ({n})",
    confirm: "Export {n} clips? That uses up to {minutes} min of your plan, the same as exporting each one.",
    confirmFree: "Export {n} clips? It’s free: this is your first video, so its exports don’t use your minutes.",
    confirmButton: "Export {n} clips",
    cancel: "Cancel",
    running: "Exporting {done} of {total}…",
    stop: "Stop",
    started: "{n} clips are exporting. Each one shows here when it is ready.",
    limit: "Your plan's minutes ran out after {n} of {total} clips.",
    stopped: "Stopped after {n} of {total}.",
    failed: "{n} couldn't be started.",
    downloading: "Downloading {n} clips…",
    dismiss: "OK",
  },

  // Duplicating a clip, and splitting one in two (ClipWorkspace)
  clipCopy: {
    editCopy: "Edit a copy",
    editCopyHint: "The exported clip stays as it is. A copy opens here with all its edits, ready to change.",
    exportedLayers: "This clip stays as it was exported. To add or change text, B-roll, polish or music, edit a copy: it opens here with all of this clip's edits.",
    duplicate: "Duplicate",
    duplicateHint: "A copy of this clip, to try a variant or make one per platform",
    split: "Split here",
    splitHint: "Cut the clip in two where the playhead is: this clip keeps what comes before, a new clip takes the rest",
    duplicated: "Copy made. It opens here.",
    splitDone: "Split in two. The second part opens here.",
    splitTooShort: "Each part needs at least 5 seconds: move the playhead.",
    duplicateFailed: "Couldn't copy the clip",
    splitFailed: "Couldn't split the clip",
  },

  // A clip extended past the footage it was prepared over (ClipWorkspace)
  clipExtend: {
    preparing: "Preparing the extra seconds…",
    unprepared: "Not prepared yet: its framing and captions are made once the clip reaches here",
  },

  // Captions dragged on the clip stage (ClipWorkspace/CaptionDragHandle)
  captionDrag: {
    where: "{y}% up, {x}% across",
    label: "Captions: drag them anywhere",
    hint: "Drag to move",
  },

  // What a clip's score is made of (ClipWorkspace/ScoreFactors)
  scoreFactors: {
    label: "What the score is made of",
    hook: "Hook",
    hookHint: "Hook: how strongly the first 3 seconds make a scrolling viewer stop",
    flow: "Flow",
    flowHint: "Flow: whether it stands on its own, with a clear start, one complete thought and an ending that lands",
    value: "Value",
    valueHint: "Value: what the viewer gets from it, an insight, a laugh, an emotion or a payoff",
    share: "Share",
    shareHint: "Share: how likely viewers are to comment on it or send it to someone",
  },

  // Sharing an exported clip's page (ClipWorkspace/ShareClip)
  clipShare: {
    share: "Share",
    shared: "Shared",
    title: "Share this clip",
    text: "Anyone with the link can watch it on a page of its own. It isn't listed or searchable, and you can stop sharing at any time.",
    link: "The clip's link",
    copy: "Copy link",
    copied: "Link copied",
    whatsapp: "WhatsApp",
    facebook: "Facebook",
    stop: "Stop sharing",
    stopped: "Sharing stopped. The link no longer opens the clip.",
    error: "Couldn't change sharing. Try again.",
    message: "Watch this clip 👉",
    linkTitle: "Public link",
  },

  // Music under a clip (ClipWorkspace/ClipMusicPicker)
  clipMusic: {
    title: "Music",
    hint: "Music from our own library, safe to post anywhere. It plays under the voice; with studio sound on, the voice is cleaned first, so the music stays clean.",
  },

  // Text on top of a clip (ClipWorkspace/ClipTextPanel)
  clipText: {
    title: "Text",
    hint: "Words on top of the clip, each shown for a few seconds. Pick one to write it, then drag it into place on the preview.",
    add: "Add text at the playhead",
    placeholder: "Your text",
    remove: "Remove this text",
    jumpTo: "Show it: {time}",
    textLabel: "Words",
    look: "Look",
    size: "Size",
    duration: "Shows for",
    seconds: "{n} s",
    empty: "No text on the clip yet.",
    dragLabel: "Text: drag it into place",
    dragHint: "Drag to place",
    looks: {
      bold: "Bold",
      box: "Card",
      outline: "Yellow",
      label: "Band",
    },
  },

  // The account's brand kit (ClipWorkspace/BrandKitPanel)
  brandKit: {
    title: "Brand kit",
    hint: "Your logo on every clip you export, and the caption style your clips start with. It is for all your clips, not only this one.",
    logo: "Your logo",
    addLogo: "Add your logo",
    replace: "Replace the logo",
    remove: "Remove the logo",
    showLogo: "Show my logo on my clips",
    corner: "Corner",
    size: "Size",
    opacity: "Opacity",
    useStyle: "Start new clips with this clip's caption style",
    styleSaved: "Clips without a style of their own use your saved caption style.",
    forgetStyle: "Forget it",
    tooBig: "The logo can be 2 MB at most.",
    notPicture: "The logo must be a PNG, JPEG or WebP picture.",
    failed: "Couldn't save the brand kit. Try again.",
    corners: {
      "top-left": "Top left",
      "top-right": "Top right",
      "bottom-left": "Bottom left",
      "bottom-right": "Bottom right",
    },
  },

  // The clip inspector's tabs (ClipWorkspace/ClipInspector)
  inspectorTabs: {
    broll: "B-roll",
    label: "Tools",
    captions: "Captions",
    edit: "Edit",
    text: "Text",
    polish: "Polish",
    music: "Music",
    brand: "Brand",
  },

  // A clip in another language (ClipWorkspace/DubPanel)
  dub: {
    versions: "Versions",
    originalTitle: "Original",
    dubbed: "Dubbed",
    add: "Add a language",
    remixOriginal: "Remix the original",
    remixDub: "Remix the {language} dub",
    state: {
      ready: "Ready",
      failed: "Failed",
      stale: "Out of date",
    },
    action: "Dub",
    tooltip: "Hear this clip in another language",
    dialogTitle: "Dub this clip",
    title: "Dub",
    intro: "Hear this clip in another language, each person in a voice of their own. The captions follow.",
    loading: "Looking at the clip…",
    noWords: "This clip has no words to dub yet.",
    into: "Dub into",
    voicesFor: "Voices in {language}",
    voice: "Voice",
    voiceFor: "Voice for {name}",
    sample: "Hear {voice}",
    stopSample: "Stop {voice}",
    listening: "Choosing a voice for each person…",
    cost: "Uses {minutes} min of your plan",
    start: "Dub into {language}",
    cancel: "Cancel",
    export: "Export",
    exporting: "Exporting…",
    download: "Download",
    failed: "The dub didn't work. Try again: it used none of your minutes.",
    tryAgain: "Try again",
    stale: "The clip changed after this dub was made. Dub it again to match.",
    again: "Dub again",
    remove: "Remove the {language} dub",
    tooLong: "Clips up to 5 minutes can be dubbed.",
    exportFailed: "The export didn't work. Try again.",
    steps: {
      starting: "Getting ready",
      translating: "Translating",
      voicing: "Recording the voices",
      fitting: "Fitting the timing",
      mixing: "Mixing the sound",
      saving: "Saving",
    },
  },

  // A shot's layout in framing (ClipWorkspace/FramingMode)
  framingLayout: {
    fit: "Fit",
    fitHint: "Show the whole picture in this shot, over a soft copy of itself: for wide shots, screens and games",
    fill: "Fill",
    fillHint: "Crop this shot to fill the frame again",
  },

  // B-roll over a clip (ClipWorkspace/ClipBrollPanel)
  autoZoom: {
    title: "Auto zoom",
    off: "Off",
    subtle: "Subtle",
    punchy: "Punchy",
    hint: "The picture moves in and out as each sentence starts, so a talking head never sits still: a slow push, or a cut in close.",
  },
  captionEmoji: {
    label: "Emoji in captions",
    hint: "An emoji under the captions, chosen by AI for the words that call for one, popping in as the word is said. Each also shows in the transcript.",
  },
  // What is on a clip, in each tab that layers something on it (ClipWorkspace/LayerHeader)
  clipLayers: {
    onClip: "On the clip · {count}",
    onClipOf: "On the clip · {count} of {max}",
    removeAll: "Remove all",
    addMore: "Add more",
    putBack: "Put them back",
    kinds: {
      titleCard: "Title card",
      text: "Text",
      broll: "B-roll",
      music: "Music",
    },
    moment: "Moment",
    sticker: "Sticker",
    zoom: "Zoom",
    inCut: "starts inside a cut, so it shows once the cut ends",
    trackLabel: "What's on the clip",
    removed: {
      broll: "B-roll removed from the clip: {count}",
      texts: "Text removed from the clip: {count}",
      moments: "Moments removed from the clip: {count}",
    },
  },
  // Leaving the clip workspace for the full editor (ClipWorkspace)
  openEditor: {
    title: "Open in the full editor?",
    text: "The full editor opens with this clip's framing and caption fixes. These stay here and don't come along: {what}.",
    stay: "Stay here",
    go: "Open it anyway",
  },
  // The row of the clip's tools under Export (ClipWorkspace/ClipInspector)
  clipTools: {
    label: "The clip's tools",
    captions: "Captions",
    editor: "Full editor",
  },
  clipBroll: {
    title: "B-roll",
    hint: "Stock video over the clip while the speaker keeps talking: the AI finds the moments that call for a picture, or search for one and put it at the playhead.",
    find: "Find B-roll with AI",
    finding: "Finding B-roll…",
    noneFound: "No moments for B-roll found in this clip.",
    added: "New shots on the clip: {count}",
    noneNew: "No new moments for B-roll: the clip already has the ones the AI found.",
    full: "The clip has {max} shots, the most it takes. Remove one to add another.",
    findFailed: "Couldn't find B-roll, try again.",
    busy: "Stock video is busy right now, try again in a few minutes.",
    tooMany: "That's a lot of B-roll ideas for one hour, try again later.",
    empty: "No B-roll on the clip yet.",
    jumpTo: "Show it: {time}",
    length: "How long it shows",
    credit: "by {name} on Pexels",
    remove: "Remove this shot",
    searchLabel: "Search stock video",
    searchPlaceholder: "City at night, coffee, rocket…",
    search: "Search",
    searchFailed: "Couldn't search, try again.",
    results: "Shots found",
    addHere: "Put it at the playhead",
    noResults: "Nothing found, try other words.",
    pexels: "Videos from Pexels, free to use.",
  },
  // The video while it is being made (components/AIAgent/making/MakingView.jsx)
  making: {
    tipsTitle: "While you wait",
    tips: [
      "Once it is ready, you can change any line, picture or voice.",
      "Pick the captions and the music on the finished video, and see it change at once.",
      "Export is free. Then share it straight to TikTok, Reels or Shorts.",
      "Share its link: when a friend who comes from it pays, you get a Pro video.",
    ],
    title: "Making your video",
    yourIdea: "Your idea",
    scenes: "Scenes ({n})",
    voiced: "Narration recorded",
    listen: "Listen",
    playing: "Playing",
    stop: "Stop",
    drawing: "Drawing the pictures…",
    pictures: "{n} pictures",
    picture: "1 picture",
    elapsed: "{time} so far",
    sceneOf: "Scene {n} of {m}",
    tabTitle: "{pct}% · {title}",
    tabReady: "✓ Ready · {title}",
    readyNotice: "Your video is ready",
    leaveEmail: "You can leave this page. We will email you when your video is ready.",
    leaveNoEmail: "You can leave this page. Your video will be here when it is ready.",
    notify: "Notify me when it is ready",
    notifyOn: "We will notify you here when it is ready.",
    left: {
      minutes: "About {n} min left",
      minute: "About a minute left",
      almost: "Almost there",
    },
    steps: {
      idea: {
        title: "Reading your idea",
        body: "Working out the language, the length and the look.",
      },
      story: {
        title: "Writing the story",
        body: "Finding the hook, planning each scene and checking the facts. This is the longest step.",
      },
      make: {
        title: "Drawing and recording",
        body: "Each picture is drawn and every line is narrated.",
      },
      finish: {
        title: "Final touches",
        body: "Captions, sound effects, music and motion.",
      },
    },
  },
};
